import * as ko from "knockout";
import {
    ICompany,
    IMessageData,
    IRouteDefinitionParams,
    IRule,
    IRuleset,
    TBaseOrObservableArrayType,
    TBaseOrObservableType,
    TDataSourceType,
} from "../../../index";
import PromiseRequest from "../../../req/PromiseRequest";
import {ObjectTypeId} from "../../../constants";
import {_} from 'lodash';
import {global, lm} from "../../../appContext";
import {nsRuleset} from "../nsRuleset";
import {ErrorTracker} from "../../../error.tracker";
import {MutableRule} from "./rule/MutableRule";
import {KOSubscriptionTracker} from "../../../util/KOSubscriptionTracker";
import {AvailableData} from "./rule/AvailableData";
import {IFullObjectType, IRulesetMutable} from "../index";
import {TServiceName} from "../../../constants/ApiServices";
import {nsRoute} from "../../../util/route";
import {ErrorObject} from "../../../error/ErrorObject";
import {ErrorManager} from "../../../error/ErrorManager";
import {_d, createErrorMessage, getAnyValue, getObjectType} from "../../../util/misc";
import {ERROR_MESSAGE, REQUEST_MESSAGE} from "../../../constants/Language";
import {
    getCancelButton,
    getDiscardButton,
    getParent,
    getSubmitButton,
    setRouteHash,
} from "../../../constants/RouteDefinitions";
import {ButtonManager} from "../../../util/ButtonManager";
import {EMPTY_HEADER_ACTIONS} from "../../page-shell/pageShellHeaderActions";
import {nsFa} from "../../fa/fn";
import ParsedRouteDataDecorator = nsRoute.ParsedRouteDataDecorator;
import getFullObjectType = nsRuleset.getFullObjectType;
import invalidateCachesAfterRulesetMutation = nsRuleset.invalidateCachesAfterRulesetMutation;
import i = nsFa.i;
import mutableHtml from './mutable.html?raw';

export let template: string = mutableHtml;

export interface IMutableRulesetParams extends IRouteDefinitionParams {
    canChangeObjectType?: TBaseOrObservableType<boolean>
    objectTypeId?: TBaseOrObservableType<ObjectTypeId> | TBaseOrObservableArrayType<ObjectTypeId>
}

export abstract class MutableRuleset extends ParsedRouteDataDecorator {
    private MAX_DESCRIPTION_LENGTH = nsRuleset.MAX_DESCRIPTION_LENGTH
    et: ErrorTracker = global.errorAreas.get("ruleset.mutable");

    messageData: KnockoutObservable<IMessageData | null> = ko.observable(null);
    isReady: KnockoutObservable<boolean> = ko.observable(false);

    protected submitRequestMessage!: string;
    protected submitSuccessMessage!: string;
    protected submitErrorMessage: string = ERROR_MESSAGE;
    protected reqSubmit!: PromiseRequest;
    protected reqGetCompany: PromiseRequest = global.getPromiseRequest('getcompany');

    // the current mutable ruleset
    data: IRulesetMutable = {
        $original: {
            authdatasources: '',
            companyid: '',
            departmentexpression: '',
            description: '',
            evaluatepositions: false,
            id: 0,
            name: "",
            objecttype: getObjectType(ObjectTypeId.User),
            refs: {},
            rules: []
        },
        authdatasources: ko.observable('' as TDataSourceType),
        companyid: ko.observable(''),
        departmentexpression: ko.observable(''),
        description: ko.observable(''),
        evaluatepositions: ko.observable(false),
        name: ko.observable(""),
        objectType: ko.observable<IFullObjectType | null>(null),
        // any rule added to this array should have error tracker initialized
        // e.g. rule.initErrorTracking()
        rules: ko.observableArray<MutableRule>([]),
    };

    protected kosub: KOSubscriptionTracker = new KOSubscriptionTracker();

    protected allRulesets!: IRuleset[];

    private reqGetConditions = global.getPromiseRequest("getconditions");
    protected company!: ICompany;
    protected objectTypes!: IFullObjectType[];

    private errName: ErrorObject;
    private errDescription: ErrorObject;
    private errObjectType: ErrorObject;

    protected errManagerInfo!: ErrorManager
    protected errManagerRules!: ErrorManager
    protected availableData: AvailableData
    protected initialObjectTypeId!: ObjectTypeId

    private readonly pageButtons = new ButtonManager()

    constructor(protected params: IMutableRulesetParams) {
        super(params);

        this.initErrorManagers()
        this.availableData = new AvailableData({
            errManagerInfo: this.errManagerInfo,
            errManagerRules: this.errManagerRules,
            ruleset: this
        })

        this.messageData({message: REQUEST_MESSAGE});

        const cancelButton = getCancelButton(() => {
            if (this.isChanged()) {
                this.showDiscardChangesConfirm(() => this.exitToViewMode());
                return;
            }
            this.exitToViewMode();
        });
        if (cancelButton) {
            this.pageButtons.add(cancelButton);
        }
        this.pageButtons.add(getDiscardButton({
            onClick: () => this.onDiscardClick(),
            enabled: () => this.isChanged(),
        }));
        this.pageButtons.add(getSubmitButton({
            onClick: () => this.onSubmit(),
            enabled: () => this.isValid() && this.isChanged(),
        }));
        // Page-shell tab row hosts preview; dialogs have no shell header, so keep it with page buttons.
        if (this.__dialog || !this.__tabManager) {
            this.pageButtons.add({
                icon: 'fa-eye',
                label: 'preview',
                cssClass: 'action',
                order: nsRoute.ButtonOrder.PREVIEW,
                onClick: () => this.onPreviewClick(),
                enabled: () => this.isPreviewValid() && !this.isPreviewWaiting(),
            });
        } else {
            this.__tabManager.contentHeaderActions({
                quick: [{
                    icon: 'fa-eye',
                    label: 'preview',
                    cls: 'btn-action',
                    onClick: () => this.onPreviewClick(),
                    enabled: () => this.isPreviewValid() && !this.isPreviewWaiting(),
                }],
                more: [],
            });
        }

        this.errName = new ErrorObject({name: 'name'});
        this.errName.addValidator({
            fn: () => !!this.data.name(),
            message: `((invalid)) ((name|ac))`,
        })
        this.errName.addValidator({
            fn: () => !/[<>]/gm.test(this.data.name()),
            message: `((contains invalid characters))`,
        })
        this.errManagerInfo.addErrorObject(this.errName);

        this.errDescription = new ErrorObject({name: 'description'});
        this.errDescription.addValidator({
            fn: () => this.data.description()?.length <= nsRuleset.MAX_DESCRIPTION_LENGTH,
            message: `((invalid)) ((length|ac))`,
        })
        this.errDescription.addValidator({
            fn: () => !/[<>]/gm.test(this.data.description()),
            message: `((contains invalid characters))`,
        })
        this.errManagerInfo.addErrorObject(this.errDescription);

        this.errObjectType = new ErrorObject({name: 'objectType'});
        this.errObjectType.addValidator({
            fn: () => !!this.data.objectType(),
            message: `((invalid)) ((object type|ac))`,
        })
        this.errManagerInfo.addErrorObject(this.errObjectType);
    }

    protected initErrorManagers() {
        this.errManagerInfo = this.__tabManager!.getTab('rulesetinfo')!.errorManager;
        this.errManagerRules = this.__tabManager!.getTab('rulesetrules')!.errorManager;
    }

    protected init(): Promise<void> {
        return Promise.resolve()
            .then(() => this.availableData.init())
            .then(() => this.initReq())
    }

    protected initReq(): Promise<void> {
        return this.reqGetCompany
            .request()
            .then((data: [ICompany]) => {
                this.company = data[0];
            });
    }

    protected initObjectTypes() {
        let objectTypeIds: ObjectTypeId[]
        if (getAnyValue(this.params.canChangeObjectType) ?? true) {
            const paramsObjectTypeId = getAnyValue(this.params.objectTypeId)
            if (paramsObjectTypeId) {
                if (Array.isArray(paramsObjectTypeId)) {
                    objectTypeIds = paramsObjectTypeId
                } else {
                    objectTypeIds = [paramsObjectTypeId]
                }
            } else {
                objectTypeIds = [ObjectTypeId.User, ObjectTypeId.Department, ObjectTypeId.Group];
                if (this.company.eformforeadm) {
                    objectTypeIds.push(ObjectTypeId.FormData);
                }
                objectTypeIds.push(ObjectTypeId.ClientCredential);
            }
        } else {
            objectTypeIds = [this.initialObjectTypeId]
        }
        this.objectTypes = objectTypeIds.map(getFullObjectType);
    }

    isValid = ko.pureComputed((): boolean => this.errManagerInfo.validate() && this.errManagerRules.validate());
    isPreviewValid = ko.pureComputed((): boolean => this.errObjectType.validate() && this.errManagerRules.validate());
    private isPreviewWaiting: KnockoutObservable<boolean> = ko.observable(false);
    isChanged = ko.pureComputed((): boolean => this._isChanged());

    protected _isChanged(): boolean {
        const a = this.data.name() != this.data.$original.name;
        const b = !_.isEqual(this.data.objectType()?.id, this.data.$original.objecttype?.id);
        const c = this.data.$original.description != this.data.description();
        const oldRules = this.data.$original.rules;
        const newRules = this.data.rules().map(item => item.toStatic());
        const d = !_.isEqual(oldRules, newRules);
        const e = this.data.$original.departmentexpression != this.data.departmentexpression()
        const f = this.data.$original.evaluatepositions != this.data.evaluatepositions()
        const g = this.data.$original.authdatasources != this.data.authdatasources();
        return a || b || c || d || e || f || g
    }

    protected request(serviceName: TServiceName, params: any): Promise<any> {
        return new PromiseRequest(serviceName).request(params);
    }

    onSubmit() {
        this.isReady(false);
        this.messageData({message: this.submitRequestMessage});

        this.enforeAndAndIfAppropriate()

        let data = this.getSubmitData();
        data = this.trimData(data)
        let json = JSON.stringify(data);

        this.reqSubmit.abort();

        Promise
            // first delete rules...
            .all(this.getDeleted()
                .map((item: IRule) => this.request("deleterule", {id: item.id})))
            // then create/update ruleset
            .then(() => this.reqSubmit.request({jruleset: json}))
            // keep the changes
            .then((data: IRuleset[]) => {
                const ruleset = data[0];
                if (ruleset) {
                    this.data.$original = ruleset;
                }
            })
            // then add
            .then(() => Promise
                .all(this.getAdded()
                    .map((item: IRule) => {
                        const _item = this.trimRule(item)
                        _item.id = 0;
                        const jrule = JSON.stringify(_item);
                        return this.request("createrule", {
                            rulesetid: this.data.$original.id,
                            jrule: jrule,
                        })
                    })
                ))
            // then update
            .then(() => Promise
                .all(this.getUpdated()
                    .map((item: IRule) => {
                        const _item = this.trimRule(item)
                        const jrule = JSON.stringify(_item);
                        return this.request("updaterule", {jrule: jrule})
                    })
                ))
            // remove cache and show details page
            .then(() => {
                invalidateCachesAfterRulesetMutation();
                global.snackbarData({message: this.submitSuccessMessage});
                if (this.__dialog) {
                    if (this.__dialog.callbackFn) {
                        this.__dialog.callbackFn(this.getSubmitDataWithRules())
                    } else {
                        _d.warn(`No callback given for dialog`)
                    }
                    this.__dialog?.close?.()
                } else {
                    setRouteHash('rulesetdetails', {id: this.data.$original.id});
                }
            })
            .catch((reason: string) => {
                this.messageData(createErrorMessage(String(reason)))
                this.isReady(true);
            });
    }

    private enforeAndAndIfAppropriate() {
        if (this.doRulesContainOr()) {
            return
        }

        this.data.rules().forEach((rule: MutableRule) => {
            if (rule.booleanoperator() === "And") {
                rule.booleanoperator("AndAnd");
            }
        });
    }

    private doRulesContainOr(): boolean {
        return this.data.rules().some(rule => {
            return ['Or', 'OrOr'].includes(rule.booleanoperator() ?? '')
        })
    }

    public getSubmitDataWithRules(): IRuleset {
        const submitData = this.getSubmitData();
        submitData.rules = this.data.rules().map((rule: MutableRule) => rule.toStatic())
        return submitData
    }

    protected getDeleted(): IRule[] {
        const original = this.data.$original.rules;
        const current = this.data.rules();
        return original
            .filter((o: IRule) => !current.find((rule: MutableRule) => o.id == rule.$original.id));
    }

    protected getAdded(): IRule[] {
        const current = this.data.rules().map((rule: MutableRule) => rule.toStatic());
        return current.filter((c: IRule) => c.id < 0);
    }

    protected getUpdated(): IRule[] {
        const current = this.data.rules().map((rule: MutableRule) => rule.toStatic());
        const original = this.data.$original.rules;
        return current
            .filter((c: IRule) => original.find((o: IRule) => (c.id == o.id) && !_.isEqual(c, o)));
    }

    protected getSubmitData(): IRuleset {
        return {
            authdatasources: this.data.authdatasources(),
            companyid: this.data.$original.companyid || global.login.selectedOrganizationId() || '',
            departmentexpression: this.data.departmentexpression(),
            description: this.data.description(),
            evaluatepositions: this.data.evaluatepositions(),
            id: this.data.$original.id || 0,
            name: this.data.name(),
            objecttype: this.data.objectType()!.type,
            refs: {},
            rules: [],
        };
    }

    private trimData(data: IRuleset): IRuleset {
        return _.defaultsDeep({
            name: data.name.trim(),
            description: data.description.trim(),
        }, data)
    }

    private trimRule(data: IRule): IRule {
        if (data.attribute?.startsWith?.('[')) {
            return _.defaultsDeep({
                attribute: data.attribute.trim?.(),
            }, data)
        }
        return data
    }

    public canChangeObjectType = ko.pureComputed((): boolean => {
        return !this.data.rules().length && ko.unwrap(this.params.canChangeObjectType ?? true)
    });

    private showElement(key: keyof IRuleset | string): boolean {
        switch (key) {
            case 'evaluatepositions':
            case 'departmentexpression': {
                return this.isObjectTypeUser()
            }
        }
        return false;
    }

    private isObjectTypeUser(): boolean {
        return !!this.data.objectType() && this.data.objectType()!.id == ObjectTypeId.User
    }

    onDiscardClick() {
        const undoData = {
            name: this.data.name(),
            objectType: this.data.objectType(),
            rules: this.data.rules().map(r => r.toStatic()),
        }
        global.snackbarData({
            message: `all changes discarded`,
            undo: () => {
                this.data.rules(undoData.rules.map((r: IRule) => new MutableRule(this.availableData, r)));
                this.data.objectType(undoData.objectType);
                this.data.name(undoData.name);
            }
        });

        this.errManagerRules.discard()
        this.initData();
    }

    /** Leave editruleset / createruleset (or close the dialog) and return to view mode. */
    private exitToViewMode() {
        if (this.__dialog) {
            this.__dialog.close?.();
            return;
        }
        const originalId = this.data.$original.id;
        if (originalId) {
            setRouteHash('rulesetdetails', {id: originalId});
            return;
        }
        const parent = getParent(this.__routeDef);
        if (parent) {
            setRouteHash(parent, this.__routeParams);
        }
    }

    private showDiscardChangesConfirm(onConfirm: () => void) {
        global.dialogManager.addConfirm({
            buttons: {
                confirm: {
                    label: `yes, continue`,
                    onClick: onConfirm,
                },
                cancel: {
                    label: `no, cancel`,
                },
            },
            content: `
            <p>${lm.t('you are about to discard all changes')}</p>
            <p>${lm.t('do you wish to continue')}</p>
            `,
            title: `${i('fa-undo')} ((discard changes))`,
        });
    }

    protected initData() {
        this.data.rules().forEach(o => o.dispose());
    }

    protected onPreviewClick() {
        nsRuleset.fnPreviewRuleset(this.getSubmitDataWithRules())
    }

    private isElementEnabled(key: keyof IRuleset): boolean {
        switch (key) {
            case 'departmentexpression':
                return !this.data.evaluatepositions()
            case 'evaluatepositions':
                return !this.data.departmentexpression()
        }
        return true;
    }

    override dispose() {
        this.__tabManager?.contentHeaderActions(EMPTY_HEADER_ACTIONS);
        this.reqSubmit.abort();
        this.reqGetConditions.abort();
        this.kosub.unsuscribeAll();
    }

}
